import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDate } from "@/modules/calculator";

interface Show {
  id: number;
  date: string;
  venue: {
    id: number;
    name: string;
    city?: string | null;
    state?: string | null;
  };
  artist?: {
    id: number;
    name: string;
  } | null;
}

interface ShowPackage {
  showId: number;
  setCore: boolean;
  setAudio: boolean;
  setVideo: boolean;
}

interface PlanCalendarGridProps {
  shows: Show[];
  showPackages: Record<number, ShowPackage>;
  campaignStart?: string;
  campaignEnd?: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  shows: Show[];
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function PlanCalendarGrid({ shows, showPackages, campaignStart, campaignEnd }: PlanCalendarGridProps) {
  // State for tooltip hover
  const [hoveredDayIndex, setHoveredDayIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Get all unique months that have shows
  const monthsWithShows = useMemo(() => {
    if (shows.length === 0) return [];

    const monthSet = new Set<string>();
    shows.forEach(show => {
      const date = new Date(show.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      monthSet.add(monthKey);
    });

    return Array.from(monthSet)
      .map(key => {
        const [year, month] = key.split('-').map(Number);
        return new Date(year, month, 1);
      })
      .sort((a, b) => a.getTime() - b.getTime());
  }, [shows]);

  // Track current month by index in the monthsWithShows array
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);

  // Reset to first month when shows change
  useEffect(() => {
    setCurrentMonthIndex(0);
  }, [monthsWithShows.length]);

  // Get the actual current month date
  const currentMonth = monthsWithShows[currentMonthIndex] || new Date();

  // Helper function to generate calendar days for any month
  const generateCalendarDays = (monthDate: Date): CalendarDay[] => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Get the day of week for the first day (0 = Sunday)
    const startingDayOfWeek = firstDay.getDay();

    const days: CalendarDay[] = [];

    // Add days from previous month to fill the first week
    const prevMonthLastDay = new Date(year, month, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay.getDate() - i);
      days.push({
        date,
        isCurrentMonth: false,
        shows: []
      });
    }

    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);

      // Create ISO date string for comparison (YYYY-MM-DD) using local time
      const yearStr = date.getFullYear();
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const dateKey = `${yearStr}-${monthStr}-${dayStr}`;

      // Find shows for this date (compare ISO date strings)
      const dayShows = shows.filter(show => {
        const showDateKey = show.date.split('T')[0];
        return showDateKey === dateKey;
      });

      days.push({
        date,
        isCurrentMonth: true,
        shows: dayShows
      });
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        shows: []
      });
    }

    return days;
  };

  // Generate calendar days for the current month (screen view)
  const calendarDays = useMemo(() => {
    return generateCalendarDays(currentMonth);
  }, [currentMonth, shows]);

  // Get package color for a show
  const getPackageColor = (showId: number): string => {
    const pkg = showPackages[showId];
    if (!pkg) return "bg-gray-400";

    const activePackages = [pkg.setCore, pkg.setAudio, pkg.setVideo].filter(Boolean).length;

    if (activePackages > 1) return "bg-green-500";
    if (pkg.setVideo) return "bg-purple-500";
    if (pkg.setAudio) return "bg-blue-500";
    if (pkg.setCore) return "bg-gray-500";

    return "bg-gray-400";
  };

  // Get package label for tooltip
  const getPackageLabel = (showId: number): string => {
    const pkg = showPackages[showId];
    if (!pkg) return "No package";

    const packages: string[] = [];
    if (pkg.setCore) packages.push("SET Core");
    if (pkg.setAudio) packages.push("SET Audio");
    if (pkg.setVideo) packages.push("SET Video");

    return packages.length > 0 ? packages.join(" + ") : "No package";
  };

  // Get package badge background class for tooltip
  const getPackageBadgeClass = (showId: number): string => {
    const pkg = showPackages[showId];
    if (!pkg) return "bg-gray-400";

    const activePackages = [pkg.setCore, pkg.setAudio, pkg.setVideo].filter(Boolean).length;

    if (activePackages > 1) return "bg-green-500";
    if (pkg.setVideo) return "bg-gradient-to-r from-purple-500 to-blue-500";
    if (pkg.setAudio) return "bg-blue-500";
    if (pkg.setCore) return "bg-gray-500";

    return "bg-gray-400";
  };

  const handlePreviousMonth = () => {
    if (currentMonthIndex > 0) {
      setCurrentMonthIndex(currentMonthIndex - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonthIndex < monthsWithShows.length - 1) {
      setCurrentMonthIndex(currentMonthIndex + 1);
    }
  };

  const canGoPrevious = currentMonthIndex > 0;
  const canGoNext = currentMonthIndex < monthsWithShows.length - 1;

  if (shows.length === 0) {
    return (
      <Card className="border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Show Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
            <Calendar className="h-12 w-12 mb-2 opacity-20" />
            <p>No shows selected</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Show Calendar
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({shows.length} {shows.length === 1 ? 'show' : 'shows'})
            </span>
          </CardTitle>

          {monthsWithShows.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousMonth}
                disabled={!canGoPrevious}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm font-medium min-w-[180px] text-center">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                {monthsWithShows.length > 1 && (
                  <span className="text-xs text-muted-foreground ml-2">
                    ({currentMonthIndex + 1} of {monthsWithShows.length})
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextMonth}
                disabled={!canGoNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Screen view - current month only */}
        <div className="print:hidden">
          {/* Calendar Grid */}
          <div className="border rounded-lg overflow-visible">
            {/* Day headers */}
            <div className="grid grid-cols-7 bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 border-b-2 border-b-pink-200/30">
              {DAYS_OF_WEEK.map((day) => (
                <div
                  key={day}
                  className="p-2 text-center text-sm font-semibold text-gray-700 border-r border-pink-100/30 last:border-r-0"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 relative z-0">
              {calendarDays.map((day, index) => (
                <div
                  key={index}
                  onMouseEnter={(e) => {
                    if (day.shows.length > 0) {
                      // Clear any pending hide timeout
                      if (hideTimeoutRef.current) {
                        clearTimeout(hideTimeoutRef.current);
                        hideTimeoutRef.current = null;
                      }

                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPosition({
                        top: rect.bottom + 4,
                        left: rect.left
                      });
                      setHoveredDayIndex(index);
                    }
                  }}
                  onMouseLeave={() => {
                    // Delay hiding to allow moving to tooltip
                    hideTimeoutRef.current = setTimeout(() => {
                      setHoveredDayIndex(null);
                    }, 300);
                  }}
                  className={cn(
                    "min-h-[80px] p-2 border-r border-b last:border-r-0 transition-all duration-200 relative",
                    !day.isCurrentMonth && "bg-gray-50/50",
                    day.shows.length > 0 && "bg-gradient-to-br from-pink-50/40 via-purple-50/30 to-blue-50/40 border-l-2 border-l-pink-400/30 hover:shadow-md"
                  )}
                >
                  <div className={cn(
                    "text-sm font-medium mb-1",
                    !day.isCurrentMonth && "text-gray-400",
                    day.isCurrentMonth && "text-gray-900"
                  )}>
                    {day.date.getDate()}
                  </div>

                  {/* Show indicators - color dots */}
                  {day.shows.length > 0 && (
                    <div className="space-y-1">
                      {day.shows.slice(0, 3).map((show) => (
                        <div key={show.id}>
                          <div className="flex items-center gap-1.5">
                            <div
                              className={cn(
                                "w-2 h-2 rounded-full flex-shrink-0",
                                getPackageColor(show.id)
                              )}
                            />
                            <span className="text-[11px] text-gray-600 truncate">
                              {show.artist?.name || "Unknown"}
                            </span>
                          </div>
                        </div>
                      ))}
                      {day.shows.length > 3 && (
                        <div className="text-[10px] text-gray-500 font-medium">
                          +{day.shows.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Tooltip rendered via Portal at document root */}
        {hoveredDayIndex !== null && calendarDays[hoveredDayIndex]?.shows.length > 0 && createPortal(
          <div
            className="fixed"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              zIndex: 999999
            }}
            onMouseEnter={() => {
              // Cancel hide timeout when mouse enters tooltip
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
            }}
            onMouseLeave={() => {
              // Hide tooltip when mouse leaves
              setHoveredDayIndex(null);
            }}
          >
            <div className="bg-white rounded-lg shadow-2xl overflow-hidden border-2 border-pink-300 min-w-[250px] max-w-[300px]">
              {/* Gradient Header */}
              <div className="bg-gradient-to-r from-pink-500 to-blue-500 text-white px-3 py-2">
                <div className="font-bold text-sm">
                  {calendarDays[hoveredDayIndex].shows.length > 0
                    ? formatDate(calendarDays[hoveredDayIndex].shows[0].date)
                    : calendarDays[hoveredDayIndex].date.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        timeZone: 'UTC'
                      })
                  }
                </div>
                <div className="text-xs opacity-90">
                  {calendarDays[hoveredDayIndex].shows.length}{' '}
                  {calendarDays[hoveredDayIndex].shows.length === 1 ? 'show' : 'shows'}
                </div>
              </div>

              {/* All shows list */}
              <div className="px-3 py-2 max-h-[300px] overflow-y-auto">
                <div className="space-y-2">
                  {calendarDays[hoveredDayIndex].shows.map((show) => (
                    <div key={show.id} className="border-b border-gray-100 last:border-b-0 pb-2 last:pb-0">
                      <div className="font-semibold text-sm text-gray-900">
                        {show.artist?.name || "Unknown Artist"}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        {show.venue.name}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {show.venue.city}, {show.venue.state}
                      </div>
                      <div className="mt-1">
                        <span className={cn(
                          "inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white",
                          getPackageBadgeClass(show.id)
                        )}>
                          {getPackageLabel(show.id)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Print view - all months */}
        <div className="hidden print:block">
          {monthsWithShows.map((month, monthIdx) => {
            const monthDays = generateCalendarDays(month);
            const monthName = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            return (
              <div key={monthIdx} className="calendar-month-page">
                <h3 className="text-lg font-semibold mb-4">{monthName}</h3>

                {/* Calendar Grid */}
                <div className="border rounded-lg overflow-hidden">
                  {/* Day headers */}
                  <div className="grid grid-cols-7 bg-gray-50 border-b">
                    {DAYS_OF_WEEK.map((day) => (
                      <div
                        key={day}
                        className="p-2 text-center text-sm font-medium text-gray-600 border-r last:border-r-0"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar days */}
                  <div className="grid grid-cols-7">
                    {monthDays.map((day, index) => (
                      <div
                        key={index}
                        className={cn(
                          "min-h-[80px] p-2 border-r border-b last:border-r-0",
                          !day.isCurrentMonth && "bg-gray-50/50",
                          day.shows.length > 0 && "bg-blue-50/30"
                        )}
                      >
                        <div className={cn(
                          "text-sm font-medium mb-1",
                          !day.isCurrentMonth && "text-gray-400",
                          day.isCurrentMonth && "text-gray-900"
                        )}>
                          {day.date.getDate()}
                        </div>

                        {/* Show indicators - color dots */}
                        {day.shows.length > 0 && (
                          <div className="space-y-1">
                            {day.shows.map((show) => (
                              <div key={show.id}>
                                <div className="flex items-center gap-1.5">
                                  <div
                                    className={cn(
                                      "w-2 h-2 rounded-full flex-shrink-0",
                                      getPackageColor(show.id)
                                    )}
                                  />
                                  <span className="text-[11px] text-gray-600 truncate">
                                    {show.artist?.name || "Unknown"}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-4">
          {(() => {
            // Count shows by package type
            const packageCounts = { setCore: 0, setAudio: 0, setVideo: 0 };
            Object.values(showPackages).forEach((pkg) => {
              if (pkg.setCore) packageCounts.setCore++;
              if (pkg.setAudio) packageCounts.setAudio++;
              if (pkg.setVideo) packageCounts.setVideo++;
            });

            return (
              <>
                {packageCounts.setCore > 0 && (
                  <div className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-default">
                    <div className="w-3 h-3 rounded-full bg-gray-500 group-hover:scale-110 transition-transform duration-200"></div>
                    <span className="text-sm font-medium text-gray-700">SET Core</span>
                    <span className="ml-1 px-2 py-0.5 text-xs font-bold text-white bg-gray-500 rounded-full">
                      {packageCounts.setCore}
                    </span>
                  </div>
                )}
                {packageCounts.setAudio > 0 && (
                  <div className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-default">
                    <div className="w-3 h-3 rounded-full bg-blue-500 group-hover:scale-110 transition-transform duration-200"></div>
                    <span className="text-sm font-medium text-blue-700">SET Audio</span>
                    <span className="ml-1 px-2 py-0.5 text-xs font-bold text-white bg-blue-500 rounded-full">
                      {packageCounts.setAudio}
                    </span>
                  </div>
                )}
                {packageCounts.setVideo > 0 && (
                  <div className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-default">
                    <div className="w-3 h-3 rounded-full bg-purple-500 group-hover:scale-110 transition-transform duration-200"></div>
                    <span className="text-sm font-medium text-purple-700">SET Video</span>
                    <span className="ml-1 px-2 py-0.5 text-xs font-bold text-white bg-purple-500 rounded-full">
                      {packageCounts.setVideo}
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );
}
